import * as React from "react";
import { useTranslation } from "react-i18next";

export type BannerKind = "info" | "success" | "warning" | "error";

export type Banner = {
  id: string;
  kind: BannerKind;
  title?: string;
  message: string;
  dismissible?: boolean;
  action?: { label: string; onClick: () => void };
};

type BannerContextValue = {
  banners: Array<Banner>;
  show: (b: Omit<Banner, "id"> & { id?: string }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
};

const BannerContext = React.createContext<BannerContextValue | undefined>(
  undefined,
);

export function useBanners() {
  const ctx = React.useContext(BannerContext);
  if (!ctx) throw new Error("useBanners must be used within BannerProvider");
  return ctx;
}

export function BannerProvider({ children }: { children: React.ReactNode }) {
  const [banners, setBanners] = React.useState<Array<Banner>>([]);

  const show: BannerContextValue["show"] = (b) => {
    const id = b.id || crypto.randomUUID();
    setBanners((prev) => [{ ...b, id }, ...prev]);
    // Default auto-dismiss behavior for dismissible banners
    if (b.dismissible !== false) {
      window.setTimeout(() => {
        setBanners((prev) => prev.filter((x) => x.id !== id));
      }, 4000);
    }
    return id;
  };

  const dismiss = (id: string) =>
    setBanners((prev) => prev.filter((b) => b.id !== id));

  const clear = () => setBanners([]);

  const value = React.useMemo(
    () => ({ banners, show, dismiss, clear }),
    [banners],
  );

  return (
    <BannerContext.Provider value={value}>{children}</BannerContext.Provider>
  );
}

export function BannerContainer() {
  const { banners, dismiss } = useBanners();
  const { t } = useTranslation("common");
  if (banners.length === 0) return null;
  return (
    <div className="w-full px-4 md:px-12">
      <div className="space-y-2" role="region" aria-label={t("banner.notifications")}>
        {banners.map((b) => (
          <div
            key={b.id}
            role="status"
            className={[
              "rounded-md border p-3",
              b.kind === "success"
                ? "bg-green-50 border-green-300 text-green-900"
                : b.kind === "warning"
                  ? "bg-yellow-50 border-yellow-300 text-yellow-900"
                  : b.kind === "error"
                    ? "bg-red-50 border-red-300 text-red-900"
                    : "bg-blue-50 border-blue-300 text-blue-900",
            ].join(" ")}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                {b.title ? (
                  <div className="font-semibold">{b.title}</div>
                ) : null}
                <div className="text-sm">{b.message}</div>
              </div>
              <div className="flex items-center gap-2">
                {b.action ? (
                  <button
                    type="button"
                    className="underline text-sm"
                    onClick={b.action.onClick}
                  >
                    {b.action.label}
                  </button>
                ) : null}
                {b.dismissible !== false ? (
                  <button
                    type="button"
                    aria-label={t("banner.dismiss")}
                    className="text-sm"
                    onClick={() => dismiss(b.id)}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BannerSlot() {
  return <BannerContainer />;
}
